import { Injectable } from "@angular/core";
import {
  HomeIaDecisionActionRequest,
  HomeIaDecisionActionResponse,
  ReclassifyDocumentResponse
} from "../../models/home/dtos";

export type HomeDecisionActionExecutionKind =
  | "generic"
  | "postpone"
  | "assign"
  | "complete"
  | "discard"
  | "reevaluate";

export interface HomeDecisionActionExecution {
  kind: HomeDecisionActionExecutionKind;
  url: string;
  body: Record<string, unknown>;
}

@Injectable({ providedIn: "root" })
export class HomeDecisionActionApiExecutionService {
  resolve(
    decisionsUrl: string,
    decisionId: string,
    request: HomeIaDecisionActionRequest
  ): HomeDecisionActionExecution {
    const endpointTemplate = this.normalizeDecisionActionEndpoint(decisionId, request.endpointTemplate);
    const kind = this.resolveDecisionActionKind(endpointTemplate, request.action);

    return {
      kind,
      url: endpointTemplate ?? this.getDecisionActionUrl(decisionsUrl, decisionId, kind),
      body: this.buildDecisionActionBody(kind, request)
    };
  }

  toReevaluationActionResponse(
    decisionId: string,
    request: HomeIaDecisionActionRequest,
    response: ReclassifyDocumentResponse
  ): HomeIaDecisionActionResponse {
    const normalizedStatus = (response.status ?? "").trim().toLowerCase();
    const reevaluationStatus = normalizedStatus === "completed"
      ? "completed"
      : normalizedStatus === "failed"
        ? "failed"
        : normalizedStatus === "not_required"
          ? "not_required"
          : "pending";

    return {
      decisionId,
      action: request.action ?? "reevaluate",
      actionResultType: "reevaluation_queued",
      reevaluationStatus,
      success: reevaluationStatus !== "failed",
      message: response.message ?? undefined,
      idempotencyReplay: false,
      isConflict: false
    };
  }

  private normalizeDecisionActionEndpoint(decisionId: string, endpointTemplate?: string | null): string | undefined {
    if (!endpointTemplate) {
      return undefined;
    }

    const withDecisionId = endpointTemplate.replace(/\{decisionId\}/gi, encodeURIComponent(decisionId));
    const trimmed = withDecisionId.trim();

    if (!trimmed) {
      return undefined;
    }

    return trimmed.replace(/^\/?api\//i, "").replace(/^\//, "");
  }

  private resolveDecisionActionKind(
    endpoint?: string,
    action?: string | null
  ): HomeDecisionActionExecutionKind {
    const normalizedEndpoint = (endpoint ?? "").toLowerCase();
    if (normalizedEndpoint.includes("/postpone")) {
      return "postpone";
    }
    if (normalizedEndpoint.includes("/assign")) {
      return "assign";
    }
    if (normalizedEndpoint.includes("/complete")) {
      return "complete";
    }
    if (normalizedEndpoint.includes("/discard")) {
      return "discard";
    }
    if (normalizedEndpoint.includes("/reevaluate")) {
      return "reevaluate";
    }

    const normalizedAction = (action ?? "").trim().toLowerCase();
    if (normalizedAction === "postpone" || normalizedAction.includes("snooze") || normalizedAction.includes("wait")) {
      return "postpone";
    }
    if (normalizedAction === "assign") {
      return "assign";
    }
    if (normalizedAction === "complete" || normalizedAction.includes("done")) {
      return "complete";
    }
    if (normalizedAction === "discard" || normalizedAction.includes("dismiss")) {
      return "discard";
    }
    if (normalizedAction.includes("reevaluate") || normalizedAction.includes("reclassify")) {
      return "reevaluate";
    }

    return "generic";
  }

  private getDecisionActionUrl(
    decisionsUrl: string,
    decisionId: string,
    kind: HomeDecisionActionExecutionKind
  ): string {
    switch (kind) {
      case "postpone":
        return `${decisionsUrl}/${decisionId}/postpone`;
      case "assign":
        return `${decisionsUrl}/${decisionId}/assign`;
      case "complete":
        return `${decisionsUrl}/${decisionId}/complete`;
      case "discard":
        return `${decisionsUrl}/${decisionId}/discard`;
      case "reevaluate":
        return `${decisionsUrl}/${decisionId}/reevaluate`;
      case "generic":
      default:
        return `${decisionsUrl}/${decisionId}/action`;
    }
  }

  private buildDecisionActionBody(
    kind: HomeDecisionActionExecutionKind,
    request: HomeIaDecisionActionRequest
  ): Record<string, unknown> {
    const payload = this.toPayloadRecord(request.payload);
    const payloadTemplate = this.toPayloadRecord(request.payloadTemplate);

    switch (kind) {
      case "postpone": {
        const days = this.toPostponeDays(payload["days"], request.snoozedUntil, request.waitingUntil);
        return this.compactPayload({
          ...payloadTemplate,
          ...payload,
          days,
          reason: payload["reason"] ?? request.reason ?? payloadTemplate["reason"],
          notes: payload["notes"] ?? request.notes ?? payloadTemplate["notes"]
        });
      }
      case "assign":
        return this.compactPayload({
          ...payloadTemplate,
          ...payload,
          assignedToUserId: payload["assignedToUserId"] ?? request.assignedToUserId ?? payloadTemplate["assignedToUserId"],
          reason: payload["reason"] ?? request.reason ?? payloadTemplate["reason"],
          notes: payload["notes"] ?? request.notes ?? payloadTemplate["notes"]
        });
      case "complete":
        return this.compactPayload({
          ...payloadTemplate,
          ...payload,
          reason: payload["reason"] ?? request.reason ?? payloadTemplate["reason"],
          notes: payload["notes"] ?? request.notes ?? payloadTemplate["notes"],
          completionEvidence:
            payload["completionEvidence"]
            ?? request.completionEvidence
            ?? payloadTemplate["completionEvidence"]
        });
      case "discard":
        return this.compactPayload({
          ...payloadTemplate,
          ...payload,
          reason: payload["reason"] ?? request.reason ?? payloadTemplate["reason"],
          notes: payload["notes"] ?? request.notes ?? payloadTemplate["notes"]
        });
      case "reevaluate":
        return this.compactPayload({
          ...payloadTemplate,
          ...payload,
          reason: payload["reason"] ?? request.reason ?? payloadTemplate["reason"] ?? payload["correctionReason"],
          notes: payload["notes"] ?? request.notes ?? payloadTemplate["notes"]
        });
      case "generic":
      default:
        return this.compactPayload({
          action: request.action ?? undefined,
          notes: request.notes ?? undefined,
          reason: request.reason ?? undefined,
          assignedToUserId: request.assignedToUserId ?? undefined,
          snoozedUntil: request.snoozedUntil ?? undefined,
          waitingUntil: request.waitingUntil ?? undefined,
          blockedReason: request.blockedReason ?? undefined,
          missingInformation: request.missingInformation,
          completionEvidence: request.completionEvidence,
          clientTraceId: request.clientTraceId ?? undefined
        });
    }
  }

  private toPayloadRecord(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return {};
    }

    return { ...(value as Record<string, unknown>) };
  }

  private compactPayload(payload: Record<string, unknown>): Record<string, unknown> {
    return Object.entries(payload).reduce<Record<string, unknown>>((accumulator, [key, value]) => {
      if (value === undefined || value === null || value === "") {
        return accumulator;
      }
      accumulator[key] = value;
      return accumulator;
    }, {});
  }

  private toPostponeDays(
    value: unknown,
    snoozedUntil?: string | null,
    waitingUntil?: string | null
  ): number {
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.max(1, Math.ceil(value));
    }

    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return Math.max(1, Math.ceil(parsed));
      }
    }

    const fallbackDate = snoozedUntil ?? waitingUntil;
    if (fallbackDate) {
      const target = new Date(fallbackDate);
      if (!Number.isNaN(target.getTime())) {
        const deltaMs = target.getTime() - Date.now();
        return Math.max(1, Math.ceil(deltaMs / (24 * 60 * 60 * 1000)));
      }
    }

    return 1;
  }
}
